<template>
  <div
    class="is-fullheight is-padding-xsmall has-background-white vadProgramMapVue is-border-left is-border-right is-border-bottom"
    id="stock-carte">
    <div
      class="loader-overlay is-radius-large"
      v-if="mapLoading"
      id="map-loader-overlay">
      <spinner-round
        :loading="mapLoading"
        v-if="mapLoading"></spinner-round>
      <div
        class="columns is-vcentered is-min-height-full"
        v-if="!mapLoading && (!programs || !programs.length)">
        <div class="column has-text-centered">
          <h1 class="title is-2 is-paddingless-bottom is-margin-bottom-small has-text-black">
            Aucun résultat
          </h1>
          <p class="is-paddingless-top is-margin-top-small has-text-black">
            Changez vos critères de filtrage
          </p>
        </div>
      </div>
    </div>
    <div
      class="vadVueMapContainer"
      id="map"
      ref="mapbox"
      style="min-height: 600px;">
      <button
        class="button"
        id="satellite-button"
        @click="toggleVisibility('mapbox-satellite')">
        <span class="icon"><i :class="{'fal fa-check': activeSatellite}"></i></span><span>Satellite</span>
      </button>
    </div>
    <program-card
      v-if="selectedProgram"
      :from-map="true"
      :program="selectedProgram"
      :source-lots="sourceLots"
      @update-card-content="displayPopup"
      class="card map-info map-info--large columns is-gapless"
      id="card-content-original"
      style="display:none;"></program-card>
  </div>
</template>

<script>
import { DefaultValues } from '@/mixins'
import { cleanQuery } from '@/utils'
import { debounce } from 'lodash'
import mapboxgl from 'mapbox-gl'

export default {
  name: 'StockMap',
  components: {
    ProgramCard: () => import('./ProgramCard')
  },
  mixins: [DefaultValues],
  props: {
    sourcePrograms: Array,
    sourceLots: Array,
    filters: Object,
    loading: Boolean
  },
  data () {
    return {
      activeSatellite: false,
      bounds: [[10.5, 51.2], [-4.55, 40.5]],
      mapboxToken: 'pk.eyJ1IjoidGltb3RoZWViYXQiLCJhIjoiY2p0OGh6eHdoMDhkNzN5bXI0cDVpMXhpbyJ9.QYQ0e-81WZO0oFsiSH6LzA',
      mapboxStyle: 'mapbox://styles/timotheebat/cjye4bl2p3oy11cs29yu04fkw?optimize=true',
      mapbox: null,
      // used to display overlay when the layers are loading on region mode change
      layersNotReady: true,
      layersNational: ['countPoint', 'countLabel'],
      layersRegional: ['clusters', 'cluster-count', 'unclustered-point'],
      popup: null,
      popupAnchor: {},
      // id of the programe to generate the popup content from programCard component
      selectedProgram: null,
      show: false,
      zoom: 5
    }
  },
  computed: {
    programs () {
      return this.sourcePrograms
    },
    mapLoading () {
      return this.layersNotReady
    }
  },
  watch: {
    loading (v) {
      // show regional markers
      if (!v && this.programs) {
        if (this.mapbox.hasImage('pulsing-dot')) {
          this.setupProgramMarkers(this.layersRegional, 'geojsonRegional')
        } else if (this.mapbox) {
          this.addAnimatedImage()
          this.setupProgramMarkers(this.layersRegional, 'geojsonRegional')
        }
        if (this.popup !== null && this.popup.isOpen()) this.popup.remove()
        // if turning back from another page, set map to previous bounds
        if (this.$route.query.bounds === undefined) this.initMapBounds()
      }
    }
  },
  created () {
    if (this.$route.query.bounds !== undefined) {
      let sw = this.$route.query.bounds[0].split(',')
      let ne = this.$route.query.bounds[1].split(',')
      this.bounds = [[parseFloat(sw[0]), parseFloat(sw[1])], [parseFloat(ne[0]), parseFloat(ne[1])]]
    }
  },
  mounted () {
    this.setMap()
  },
  methods: {
    historySave: debounce(function () {
      let query = {
        ...this.filters,
        bounds: this.bounds
      }
      let queryString = cleanQuery(query)
      history.pushState(null, null, `#${this.$route.path}?${queryString}`)
    }, 500),
    initMapBounds () {
      if (!this.programs || (this.programs && !this.programs.length)) return
      let bounds = new mapboxgl.LngLatBounds()
      let index = 0
      for (let p of this.programs) {
        ++index
        if (p.address && p.address.longitude && p.address.latitude) {
          if (p.address.latitude > 51 || p.address.latitude < 41.3 || p.address.longitude > 10 || p.address.longitude < -5) {
          } else {
            bounds.extend(new mapboxgl.LngLat(p.address.longitude, p.address.latitude))
          }
        }
        let maxZoom = 18
        if (this.programs.length <= 2) maxZoom = 8
        if (index === this.programs.length) this.mapbox.fitBounds(bounds, { maxZoom, padding: { top: 130, bottom: 180, left: 150, right: 150 } })
      }
    },
    setMap () {
      mapboxgl.accessToken = this.mapboxToken
      const mapConfig = {
        container: 'map',
        style: this.mapboxStyle,
        zoom: this.zoom,
        bounds: this.bounds,
        maxBounds: [{ lng: -9, lat: 40.5 }, { lng: 14.6, lat: 51.2 }],
        maxZoom: 19,
        minZoom: 4,
        scrollZoom: false
      }
      if (!mapboxgl.supported()) {
        alert('Your browser does not support Mapbox GL')
      } else {
        this.mapbox = new mapboxgl.Map(mapConfig)
        const navigationControl = new mapboxgl.NavigationControl({
          showCompass: false,
          showZoom: true
        })
        this.mapbox.addControl(navigationControl, 'top-right')
        this.mapbox.on('load', () => {
          this.addAnimatedImage()
          this.set3DLayer()
          this.popup = new mapboxgl.Popup({
            closeButton: false,
            className: 'stock-popup',
            closeOnClick: false,
            offset: 15,
            maxWidth: 'none'
          })
          if (this.$route.query.bounds !== undefined) this.mapbox.zoomOut()
        })
        let formerZoomIn
        if (this.mapbox.loaded()) formerZoomIn = this.mapbox.getZoom()
        this.mapbox.on('zoomend', () => {
          let bounds = this.mapbox.getBounds()
          this.bounds = [[bounds._sw.lng, bounds._sw.lat], [bounds._ne.lng, bounds._ne.lat]]
          this.historySave()
          let zoom = this.mapbox.getZoom()
          if (zoom >= 16 && zoom <= 17 && zoom > formerZoomIn) {
            this.mapbox.easeTo({ duration: 1000, pitch: 45, zoom: 17 })
          }
          if (zoom >= 16 && zoom <= 17 && zoom < formerZoomIn) {
            this.mapbox.easeTo({ pitch: 0, zoom: 16 })
          }
          formerZoomIn = zoom
          if (zoom < 17 && this.mapbox.getPitch() !== 0) this.mapbox.easeTo({ pitch: 0 })
          if (zoom > 17 && this.mapbox.getPitch() === 0) this.mapbox.easeTo({ pitch: 45 })
        })
      }
      this.setupProgramMarkers(this.layersRegional, 'geojsonRegional')
    },
    set3DLayer () {
      this.mapbox.addLayer({
        id: '3D',
        source: 'composite',
        'source-layer': 'building',
        filter: [ '==', 'extrude', 'true' ],
        type: 'fill-extrusion',
        minzoom: 15,
        layout: { visibility: 'visible' },
        paint: {
          'fill-extrusion-color': '#eeeeee',
          // use an 'interpolate' expression to add a smooth transition effect to the buildings as the user zooms in
          // 'fill-extrusion-height': [ 'interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'height'] ],
          // 'fill-extrusion-base': [ 'interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'min_height'] ],
          'fill-extrusion-opacity': 0.6
        }
      }, 'mapbox-satellite')
    },
    addAnimatedImage () {
      let _this = this
      let size = 150
      let pulsingDot = {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),
        onAdd: function () {
          let canvas = document.createElement('canvas')
          canvas.width = this.width
          canvas.height = this.height
          this.context = canvas.getContext('2d')
        },
        render: function () {
          let duration = 1000
          let t = (performance.now() % duration) / (duration * 1.5)
          let radius = size / 3 * 0.3
          let outerRadius = size / 2 * 0.7 * t + radius
          let context = this.context
          // draw outer circle
          context.clearRect(0, 0, this.width, this.height)
          context.beginPath()
          context.arc(this.width / 2, this.height / 2, outerRadius, 0, Math.PI * 2)
          context.fillStyle = 'rgba(255, 200, 200,' + (1 - t) + ')'
          context.fill()
          // draw inner circle
          context.beginPath()
          context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2)
          context.fillStyle = 'rgba(255, 100, 100, 1)'
          context.lineWidth = 2 + 4 * (1 - t)
          context.fill()
          // update this image's data with data from the canvas
          this.data = context.getImageData(0, 0, this.width, this.height).data
          // keep the map repainting
          _this.mapbox.triggerRepaint()
          // return `true` to let the map know that the image was updated
          return true
        }
      }
      if (!this.mapbox.hasImage('pulsing-dot')) {
        this.mapbox.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2 })
      }
    },
    toggleVisibility (layer) {
      let visibility = this.mapbox.getLayoutProperty(layer, 'visibility')
      if (visibility === 'visible') {
        this.mapbox.setLayoutProperty(layer, 'visibility', 'none')
        if (layer === 'mapbox-satellite') this.activeSatellite = false
      } else {
        this.mapbox.setLayoutProperty(layer, 'visibility', 'visible')
        if (layer === 'mapbox-satellite') this.activeSatellite = true
      }
    },
    async program2geojson () {
      let locationsList = []
      let properties = {}
      let coordinates = []
      let features = this.programs.map(p => {
        // get features to integrate to the geojson
        properties = { program: p.pk }
        // check for duplicates location
        if (locationsList.findIndex((el) => { return el[0] === p.address.longitude && el[1] === p.address.latitude }) !== -1) {
          // move sligthly duplicate location to the right
          coordinates = [p.address.longitude + 0.00042, p.address.latitude - 0.0001]
        } else {
          coordinates = [p.address.longitude, p.address.latitude]
        }
        locationsList.push(coordinates)
        return {
          type: 'Feature',
          properties,
          geometry: { type: 'Point', coordinates }
        }
      })
      return features
    },
    addProgramSource (features, source, callback) {
      this.mapbox.addSource(source, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features },
        cluster: true,
        // Max zoom to cluster points on
        clusterMaxZoom: 15
        // clusterRadius: 50 // Radius of each cluster when clustering points (defaults to 50)
      })
      if (callback) callback()
    },
    addProgramSourceNlayers (layers, source, features) {
      for (let layer of layers) {
        if (this.mapbox.getLayer(layer)) this.mapbox.removeLayer(layer)
      }
      if (this.mapbox.getSource(source)) this.mapbox.removeSource(source)
      this.addProgramSource(features, source)
      this.addProgramLayers()
    },
    async setupProgramMarkers (layers, source) {
      const progGeoPromise = this.program2geojson()
      let features
      try {
        features = await progGeoPromise
      } catch (e) {
        console.error('An error occured while waiting for progGeoPromise:', e)
      }
      if (this.mapbox && features !== null) {
        if (this.mapbox.isStyleLoaded()) {
          this.addProgramSourceNlayers(layers, source, features)
        } else {
          this.mapbox.once('sourcedata', () => this.addProgramSourceNlayers(layers, source, features))
        }
      }
    },
    addProgramLayers () {
      this.mapbox.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'geojsonRegional',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [ 'step', ['get', 'point_count'], '#51b2f7', 10, '#ffc354', 50, '#f87051' ],
          'circle-radius': [ 'step', ['get', 'point_count'], 17, 10, 19, 50, 21 ],
          'circle-opacity': 0.9,
          'circle-stroke-color': [ 'step', ['get', 'point_count'], '#deedf7', 10, '#fff6e5', 50, '#f7e3de' ],
          'circle-stroke-width': [ 'step', ['get', 'point_count'], 9, 10, 10, 50, 12 ],
          'circle-stroke-opacity': 0.7
        }
      })
      this.mapbox.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'geojsonRegional',
        filter: ['==', 'cluster', true],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-allow-overlap': true,
          'text-ignore-placement': true
        }
      })
      this.mapbox.addLayer({
        id: 'unclustered-point',
        type: 'symbol',
        source: 'geojsonRegional',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-image': 'pulsing-dot',
          'symbol-z-order': 'source',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-padding': 0,
          'icon-pitch-alignment': 'map'
        }
      })
      this.changeMousePointer(this.layersRegional)
      // inspect a cluster on click
      this.mapbox.on('click', 'clusters', (e) => {
        let features = this.mapbox.queryRenderedFeatures(e.point, { layers: ['clusters'] })
        let clusterId = features[0].properties.cluster_id
        this.mapbox.getSource('geojsonRegional').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return
          this.mapbox.easeTo({ center: features[0].geometry.coordinates, zoom })
        })
      })
      // open a popup on click
      this.mapbox.on('click', 'unclustered-point', (e) => {
        let features = this.mapbox.queryRenderedFeatures(e.point, { layers: ['unclustered-point'] })
        this.popupAnchor = features[0].geometry.coordinates
        if (this.programs !== undefined) this.selectedProgram = this.programs.find(x => x.pk === features[0].properties.program)
        if (this.popup !== null) this.displayPopup()
      })
      this.layersNotReady = false
    },
    displayPopup () {
      let cardContent = window.document.getElementById('card-content-original')
      if (cardContent !== null) {
        if (this.popup.isOpen()) this.popup.remove()
        const zoomBeforePopup = this.mapbox.getZoom()
        // fix too fast zoom when far away
        let duration = (5 + (20 - zoomBeforePopup)) * 100
        if (zoomBeforePopup < 14) {
          this.mapbox.flyTo({ center: this.popupAnchor, zoom: 14, duration: 1000, pitch: 45 })
          this.mapbox.once('zoomend', () => {
            this.mapbox.flyTo({ center: this.popupAnchor, zoom: 17, duration, pitch: 45 })
          })
        } else {
          this.mapbox.flyTo({ center: this.popupAnchor, zoom: 17, duration, pitch: 45 })
        }
        this.mapbox.once('moveend', () => {
          this.popup.setLngLat(this.popupAnchor)
            .setHTML(`
              <div class="custom-stock-popup-close-button">
                <span class="icon is-small is-size-4"><i class="fal fa-times-circle"></i></span>
              </div>
              <div id="card-content" class="card map-info map-info--mediumLarge"> ${cardContent.innerHTML}</div>
              `)
            .addTo(this.mapbox)
          let element = document.getElementsByClassName('custom-stock-popup-close-button')[0]
          element.addEventListener('click', () => {
            let zoom = this.mapbox.getZoom()
            if (zoomBeforePopup < zoom) zoom = zoomBeforePopup
            if (zoomBeforePopup > 16) zoom = 15.5
            this.popup.remove()
            this.mapbox.easeTo({ center: this.popupAnchor, zoom, duration: 1000, pitch: 0 })
          })
          element.addEventListener('mouseover', () => {
            document.body.style.cursor = 'pointer'
          })
          element.addEventListener('mouseleave', () => {
            document.body.style.cursor = ''
          })
        })
      }
    },
    changeMousePointer (layers) {
      for (let layer of layers) {
        this.mapbox.on('mouseenter', layer, () => {
          this.mapbox.getCanvas().style.cursor = 'pointer'
        })
        this.mapbox.on('mouseleave', layer, () => {
          this.mapbox.getCanvas().style.cursor = ''
        })
      }
    }
  }
}
</script>

<style lang="sass">
#stock-carte
  #satellite-button
    position: absolute
    align-items: center
    top: 10px
    left: 10px
    height: 28px
    padding-left: 10px
    z-index: 10
    color: #4a4a4a
    font: 12px/20px 'Helvetica Neue', Arial, Helvetica, sans-serif
  #satellite-button:hover, .mapboxgl-ctrl:hover
    background-color: #f7f7f7
  #satellite-button, .mapboxgl-ctrl-group
    box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.2) !important
  .mapboxgl-popup
    z-index: 2
  .mapboxgl-popup .custom-stock-popup-close-button
    display: flex
    position: absolute
    top: 13px
    right: 13px
    z-index: 1
  .mapboxgl-popup-content
    border-radius: 6px
    background: rgba(255, 255, 255, 0.9)
    padding: 8px 8px 9px
  .mapboxgl-popup .fa-times-circle
    color: #666
  .mapboxgl-popup .fa-times-circle:hover
    color: #202020
  .mapboxgl-canvas
    z-index: -1
  .card.map-info
    border: 0
    &.map-info--large
      max-width: 100%
      width: 65vw
  .card-header-icon
    display: none
  .card-content
    padding-bottom: 0
</style>
